import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { LocationSelector } from "@/components/LocationSelector";
import { cn } from "@/lib/utils";
import { Car } from "@shared/schemas/cars";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { Car as CarIcon, Eye, MapPin, ShieldCheck } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "wouter";

// API 응답 타입 정의
type CarListItem = Car & {
  propertyType?: string | null;
  parking_spaces?: number | null;
};
type CarsApiResponse = CarListItem[] | { ok: boolean; data?: CarListItem[] };

/**
 * API 응답을 정규화하여 항상 Car[] 배열을 반환
 * @param data API 응답 데이터 (배열 또는 {ok: true, data: [...]} 형식)
 * @returns 정규화된 Car[] 배열
 */
function normalizeCars(data: CarsApiResponse | undefined): CarListItem[] {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

// 호주 자동차 브랜드 목록
const carMakes = [
  "Toyota",
  "Hyundai",
  "Mazda",
  "Ford",
  "Mitsubishi",
  "Kia",
  "Nissan",
  "Honda",
  "Volkswagen",
  "Subaru",
  "BMW",
  "Mercedes-Benz",
  "Audi",
  "Lexus",
  "Volvo",
  "Jeep",
  "Land Rover",
  "Porsche",
  "Tesla",
  "Other",
];

// Body Type 목록
const bodyTypes = [
  "SUV",
  "Hatch",
  "Sedan",
  "Ute",
  "Van",
  "Wagon",
  "Coupe",
  "Convertible",
];

// Transmission 목록
const transmissions = ["Auto", "Manual"];

// Fuel Type 목록
const fuelTypes = ["Petrol", "Diesel", "Electric", "Hybrid", "LPG"];

// Rego Status 목록
const regoStatuses = ["Yes", "No", "Expired"];

// 호주 주요 도시 목록
const majorCities = [
  { state: "NSW", city: "Sydney", label: "시드니" },
  { state: "NSW", city: "Newcastle", label: "뉴캐슬" },
  { state: "NSW", city: "Wollongong", label: "울런공" },
  { state: "VIC", city: "Melbourne", label: "멜버른" },
  { state: "VIC", city: "Geelong", label: "질롱" },
  { state: "QLD", city: "Brisbane", label: "브리즈번" },
  { state: "QLD", city: "Gold Coast", label: "골드 코스트" },
  { state: "QLD", city: "Cairns", label: "케언스" },
  { state: "SA", city: "Adelaide", label: "애들레이드" },
  { state: "WA", city: "Perth", label: "퍼스" },
  { state: "TAS", city: "Hobart", label: "호바트" },
  { state: "NT", city: "Darwin", label: "다윈" },
  { state: "ACT", city: "Canberra", label: "캔버라" },
];

// 지역 필터 (News 페이지와 동일)
const regionFilters = [
  { id: "ALL", label: "전체" },
  { id: "NSW", label: "시드니 (NSW)" },
  { id: "VIC", label: "멜번 (VIC)" },
  { id: "QLD", label: "브리즈번/케언즈 (QLD)" },
  { id: "SA", label: "아델레이드 (SA)" },
  { id: "WA", label: "퍼스 (WA)" },
  { id: "NT", label: "다윈 (NT)" },
  { id: "ACT", label: "캔버라 (ACT)" },
];

export default function CarMarket() {
  const { user } = useAuth();
  const { selectedRegion, setSelectedRegion } = useRegion();
  const [selectedState, setSelectedState] = useState<string>("ALL");
  const [, setSelectedCity] = useState<string>("ALL");
  const [selectedSuburb, setSelectedSuburb] = useState<string>("ALL");
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  // RegionContext의 selectedRegion과 동기화
  useEffect(() => {
    if (selectedRegion) {
      if (selectedRegion === "ALL") {
        setSelectedState("ALL");
      } else {
        setSelectedState(selectedRegion);
      }
    }
  }, [selectedRegion]);

  // selectedState 변경 시 RegionContext도 업데이트
  const handleStateChange = (state: string) => {
    setSelectedState(state);
    if (state !== "ALL") {
      setSelectedRegion(state as any);
    }
  };
  const [selectedMake, setSelectedMake] = useState<string>("all");
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [selectedBodyType, setSelectedBodyType] = useState<string>("all");
  const [selectedTransmission, setSelectedTransmission] =
    useState<string>("all");
  const [selectedFuelType, setSelectedFuelType] = useState<string>("all");
  const [selectedRegoStatus, setSelectedRegoStatus] = useState<string>("all");
  const [rwcAvailable, setRwcAvailable] = useState<boolean | undefined>(
    undefined
  );
  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");
  const [minYear, setMinYear] = useState<string>("");
  const [maxYear, setMaxYear] = useState<string>("");
  const [minMileage, setMinMileage] = useState<string>("");
  const [maxMileage, setMaxMileage] = useState<string>("");
  const [sellerType, setSellerType] = useState<string>("all"); // "private" | "dealer" | "all"
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>("all"); // "new" | "used" | "sold" | "all"

  const { data: carsData, isLoading } = useQuery<CarsApiResponse>({
    queryKey: [
      "/api/cars",
      selectedState,
      selectedSuburb,
      selectedMake,
      selectedModel,
      selectedBodyType,
      selectedTransmission,
      selectedFuelType,
      selectedRegoStatus,
      rwcAvailable,
      minPrice,
      maxPrice,
      minYear,
      maxYear,
      minMileage,
      maxMileage,
      sellerType,
      selectedPropertyType,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedState !== "ALL") params.append("state", selectedState);
      if (selectedSuburb !== "ALL") params.append("suburb", selectedSuburb);
      if (selectedMake && selectedMake !== "all")
        params.append("make", selectedMake);
      if (selectedModel) params.append("model", selectedModel);
      if (selectedBodyType && selectedBodyType !== "all")
        params.append("body_type", selectedBodyType);
      if (selectedTransmission && selectedTransmission !== "all")
        params.append("transmission", selectedTransmission);
      if (selectedFuelType && selectedFuelType !== "all")
        params.append("fuel_type", selectedFuelType);
      if (selectedRegoStatus && selectedRegoStatus !== "all")
        params.append("rego_status", selectedRegoStatus);
      if (rwcAvailable !== undefined)
        params.append("rwc_available", String(rwcAvailable));
      if (minPrice)
        params.append("price_min", String(parseInt(minPrice) * 100)); // Convert to cents
      if (maxPrice)
        params.append("price_max", String(parseInt(maxPrice) * 100));
      if (minYear) params.append("year_min", minYear);
      if (maxYear) params.append("year_max", maxYear);
      if (minMileage) params.append("mileage_min", minMileage);
      if (maxMileage) params.append("mileage_max", maxMileage);
      if (sellerType && sellerType !== "all")
        params.append("seller_type", sellerType);
      if (selectedPropertyType && selectedPropertyType !== "all")
        params.append("property_type", selectedPropertyType);
      params.append("limit", "50");

      const res = await fetch(`/api/cars?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch cars");
      const json = await res.json();
      // API 응답이 {ok: true, data: {...}} 형식인 경우 처리
      if (json.ok && json.data) {
        // data가 다시 {ok: true, data: [...]} 형식일 수 있음
        if (json.data.ok && Array.isArray(json.data.data)) {
          return json.data.data;
        }
        // data가 배열인 경우
        if (Array.isArray(json.data)) {
          return json.data;
        }
      }
      return json;
    },
  });

  // API 응답을 정규화하여 항상 Car[] 배열 보장
  const cars = normalizeCars(carsData);

  const formatPrice = (priceCents: number) => {
    if (priceCents === 0) return "무료나눔";
    if (priceCents === -1) return "가격협의";
    return `$${Math.round(priceCents / 100).toLocaleString()}`;
  };

  const formatMileage = (mileage: number | null) => {
    if (!mileage) return "";
    return `${mileage.toLocaleString()}km`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">중고차</h1>
          <p className="text-muted-foreground">
            호주 교민을 위한 안전한 중고차 거래 플랫폼
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filter Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <h2 className="text-lg font-semibold">필터</h2>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* 지역 필터 - 주요 지역 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    주요 지역
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {regionFilters.map((region) => {
                      const isSelected = selectedState === region.id;
                      return (
                        <button
                          key={region.id}
                          onClick={() => handleStateChange(region.id)}
                          className={cn(
                            "text-xs px-2 py-1 rounded transition-colors",
                            isSelected
                              ? "bg-[#60a6fa] text-white"
                              : "bg-muted text-foreground hover:bg-muted/80"
                          )}
                        >
                          {region.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 지역변경 버튼 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    지역 변경
                  </label>
                  <LocationSelector
                    variant="button"
                    open={locationDialogOpen}
                    onOpenChange={setLocationDialogOpen}
                  />
                </div>

                {/* 매물 유형 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    매물 유형
                  </label>
                  <Select value={selectedPropertyType} onValueChange={setSelectedPropertyType}>
                    <SelectTrigger>
                      <SelectValue placeholder="매물 유형 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      <SelectItem value="new">새차</SelectItem>
                      <SelectItem value="used">중고차</SelectItem>
                      <SelectItem value="sold">판매완료</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Make 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    브랜드
                  </label>
                  <Select value={selectedMake} onValueChange={setSelectedMake}>
                    <SelectTrigger>
                      <SelectValue placeholder="브랜드 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      {carMakes.map((make) => (
                        <SelectItem key={make} value={make}>
                          {make}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Body Type 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">차종</label>
                  <Select
                    value={selectedBodyType}
                    onValueChange={setSelectedBodyType}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="차종 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      {bodyTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Transmission 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    변속기
                  </label>
                  <Select
                    value={selectedTransmission}
                    onValueChange={setSelectedTransmission}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="변속기 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      {transmissions.map((trans) => (
                        <SelectItem key={trans} value={trans}>
                          {trans}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Fuel Type 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">연료</label>
                  <Select
                    value={selectedFuelType}
                    onValueChange={setSelectedFuelType}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="연료 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      {fuelTypes.map((fuel) => (
                        <SelectItem key={fuel} value={fuel}>
                          {fuel}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Rego Status 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Rego 상태
                  </label>
                  <Select
                    value={selectedRegoStatus}
                    onValueChange={setSelectedRegoStatus}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Rego 상태" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      {regoStatuses.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* RWC 필터 */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rwc"
                    checked={rwcAvailable === true}
                    onCheckedChange={(checked) =>
                      setRwcAvailable(checked ? true : undefined)
                    }
                  />
                  <label htmlFor="rwc" className="text-sm font-medium">
                    RWC 보유
                  </label>
                </div>

                {/* 가격 범위 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    가격 범위 (AUD)
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                    />
                  </div>
                </div>

                {/* 연식 범위 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">연식</label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={minYear}
                      onChange={(e) => setMinYear(e.target.value)}
                      min="1900"
                      max="2100"
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={maxYear}
                      onChange={(e) => setMaxYear(e.target.value)}
                      min="1900"
                      max="2100"
                    />
                  </div>
                </div>

                {/* 주행거리 범위 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    주행거리 (km)
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={minMileage}
                      onChange={(e) => setMinMileage(e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={maxMileage}
                      onChange={(e) => setMaxMileage(e.target.value)}
                    />
                  </div>
                </div>

                {/* 판매자 유형 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    판매자 유형
                  </label>
                  <Select value={sellerType} onValueChange={setSellerType}>
                    <SelectTrigger>
                      <SelectValue placeholder="전체" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      <SelectItem value="private">개인</SelectItem>
                      <SelectItem value="dealer">딜러</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* 필터 초기화 */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setSelectedState("ALL");
                    setSelectedCity("ALL");
                    setSelectedSuburb("ALL");
                    setSelectedMake("all");
                    setSelectedModel("");
                    setSelectedBodyType("all");
                    setSelectedTransmission("all");
                    setSelectedFuelType("all");
                    setSelectedRegoStatus("all");
                    setRwcAvailable(undefined);
                    setMinPrice("");
                    setMaxPrice("");
                    setMinYear("");
                    setMaxYear("");
                    setMinMileage("");
                    setMaxMileage("");
                    setSellerType("all");
                    setSelectedPropertyType("all");
                  }}
                >
                  필터 초기화
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Car List */}
          <div className="lg:col-span-3">
            {isLoading ? (
              <div className="py-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <Card key={i}>
                      <Skeleton className="h-48 w-full" />
                      <CardHeader>
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                      </CardHeader>
                      <CardContent>
                        <Skeleton className="h-4 w-full" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <p className="text-center text-sm text-muted-foreground mt-4">
                  차량 정보를 불러오는 중입니다…
                </p>
              </div>
            ) : cars.length === 0 ? (
              <Card className="p-12 text-center">
                <CarIcon className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  조건에 맞는 차량이 없습니다.
                </p>
                {user && (
                  <Button asChild>
                    <Link href="/cars/new">중고차 등록하기</Link>
                  </Button>
                )}
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {cars.map((car) => {
                  // 이미지 URL 정규화 및 검증
                  // R2 경로(/api/media/...)와 외부 URL 모두 지원
                  const normalizeImageUrl = (url: string | null | undefined): string | null => {
                    if (!url) return null;

                    // R2 경로인 경우 그대로 반환 (이미 정규화됨)
                    if (url.startsWith('/api/media/')) {
                      return url;
                    }

                    // 절대 URL인 경우
                    try {
                      const urlObj = new URL(url);

                      // Unsplash 이미지인 경우 파라미터 정리
                      if (urlObj.hostname.includes('unsplash.com')) {
                        urlObj.searchParams.delete('h');
                        urlObj.searchParams.delete('fit');
                        if (!urlObj.searchParams.has('w')) {
                          urlObj.searchParams.set('w', '800');
                        }
                        return urlObj.toString();
                      }

                      // 다른 외부 URL도 그대로 반환
                      return url;
                    } catch {
                      // 상대 경로이거나 잘못된 URL인 경우 그대로 반환
                      return url;
                    }
                  };

                  // 이미지 배열 처리: primary_image가 있으면 첫 번째로 추가하고, images 배열과 합침
                  const carImages = (() => {
                    const imagesArray = Array.isArray(car.images) ? car.images.map(normalizeImageUrl).filter(Boolean) as string[] : [];
                    const primaryImg = normalizeImageUrl(car.primary_image || (car as any).primaryImage);

                    // primary_image가 있고 images 배열에 없으면 첫 번째로 추가
                    if (primaryImg && !imagesArray.includes(primaryImg)) {
                      return [primaryImg, ...imagesArray];
                    }

                    // primary_image가 없고 images 배열만 있으면 그대로 사용
                    if (imagesArray.length > 0) {
                      return imagesArray;
                    }

                    // primary_image만 있으면 배열로 변환
                    if (primaryImg) {
                      return [primaryImg];
                    }

                    return [];
                  })();

                  return (
                    <Link key={car.id} href={`/cars/${car.id}`}>
                      <Card className="group overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg">
                        {/* 이미지 상단 - 당근마켓 스타일 */}
                        <div className="relative w-full aspect-square overflow-hidden bg-muted">
                          {carImages.length > 0 ? (
                            <img
                              src={(() => {
                                const imgUrl = carImages[0];
                                // R2 경로인 경우 절대 URL로 변환
                                if (imgUrl && imgUrl.startsWith('/api/media/')) {
                                  return imgUrl.startsWith('http') ? imgUrl : `${window.location.origin}${imgUrl}`;
                                }
                                return imgUrl;
                              })()}
                              alt={car.make + " " + car.model}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                              onError={(e) => {
                                const img = e.currentTarget;
                                const parent = img.parentElement;
                                if (parent && !parent.querySelector(".image-placeholder")) {
                                  img.style.display = "none";
                                  const placeholder = document.createElement("div");
                                  placeholder.className = "image-placeholder w-full h-full flex items-center justify-center bg-muted";
                                  placeholder.innerHTML = `
                                    <div class="text-center">
                                      <svg class="w-16 h-16 mx-auto text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"></path>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"></path>
                                      </svg>
                                    </div>
                                  `;
                                  parent.appendChild(placeholder);
                                }
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <CarIcon className="w-16 h-16 text-muted-foreground" />
                            </div>
                          )}
                        {/* 매물 유형 배지 - 오른쪽 상단 맨 위 */}
                        {(car.property_type || car.propertyType) && (
                          <Badge
                            className={`absolute top-2 right-2 text-white backdrop-blur-sm text-[10px] px-1.5 py-0.5 font-semibold z-10 ${
                              (car.property_type || car.propertyType) === "new"
                                ? "bg-blue-600"
                                : (car.property_type || car.propertyType) === "used"
                                ? "bg-green-600"
                                : "bg-gray-600"
                            }`}
                          >
                            {(car.property_type || car.propertyType) === "new"
                              ? "새차"
                              : (car.property_type || car.propertyType) === "used"
                              ? "중고차"
                              : "판매완료"}
                          </Badge>
                        )}
                        {/* 배지 - 이미지 위 오버레이 */}
                        {car.is_dealer_listing && (
                          <Badge
                            className="absolute top-10 right-2 bg-black/50 text-white backdrop-blur-sm text-[10px] px-1.5 py-0.5"
                            variant="default"
                          >
                            딜러
                          </Badge>
                        )}
                        {car.is_premium && (
                          <Badge
                            className="absolute top-2 left-2 bg-black/50 text-white backdrop-blur-sm"
                            variant="secondary"
                          >
                            프리미엄
                          </Badge>
                        )}
                      </div>

                      {/* 정보 하단 - 당근마켓 스타일 */}
                      <CardContent className="p-4">
                        <div className="space-y-2">
                          {/* 차량명과 가격 */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-base group-hover:text-primary transition-colors line-clamp-1">
                                {car.make} {car.model}
                              </h3>
                              <p className="text-sm text-muted-foreground mt-0.5">
                                {car.year}년 · {formatMileage(car.mileage)}
                                {car.parking_spaces !== null && car.parking_spaces !== undefined && (
                                  <> · 주차 {car.parking_spaces}대</>
                                )}
                              </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="font-bold text-lg text-primary">
                                {formatPrice(car.price)}
                              </p>
                            </div>
                          </div>

                          {/* 위치 */}
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate">
                              {car.city}
                              {car.suburb && `, ${car.suburb}`}
                            </span>
                          </div>

                          {/* 하단 메타 정보 */}
                          <div className="flex items-center justify-between pt-2 border-t border-border/30">
                            <div className="flex items-center gap-2 flex-wrap">
                              {car.body_type && (
                                <Badge variant="outline" className="text-xs">
                                  {car.body_type}
                                </Badge>
                              )}
                              {car.transmission && (
                                <Badge variant="outline" className="text-xs">
                                  {car.transmission}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Eye className="h-3 w-3" />
                              <span>{car.views || 0}</span>
                              {car.created_at && (
                                <>
                                  <span>·</span>
                                  <span>
                                    {formatDistanceToNow(new Date(car.created_at), {
                                      addSuffix: true,
                                      locale: ko,
                                    })}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  );
                })}
              </div>
            )}

            {/* 등록 버튼 */}
            {user && (
              <div className="mt-8 text-center">
                <Button asChild size="lg">
                  <Link href="/cars/new">중고차 등록하기</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
